import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser, AuthState } from '@/types/auth';
import { registerAuthStore } from '@/lib/api-client';

// ---------------------------------------------------------------------------
// Inactivity timeout — env-driven (builder-prompt §3 D9).
//
// VITE_* vars are runtime-substituted by env.sh in the docker image; see
// apps/cbs-frontend/.docker/scripts/env.sh and .env.example. In dev, set them
// in apps/cbs-frontend/.env.
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 900_000; // 15 minutes
const DEFAULT_WARNING_MS = 60_000; // 60 seconds before timeout

/**
 * Parse a VITE_* numeric env var. Returns the fallback when the value is
 * missing, the env.sh placeholder literal (unsubstituted), non-numeric, or
 * non-positive. Exported for unit testing.
 */
export function parseMsEnv(raw: string | undefined, fallback: number): number {
  if (raw === undefined) return fallback;
  const trimmed = raw.trim();
  // env.sh leaves the literal placeholder string in place when the docker env
  // var is missing — guard against the placeholder being parsed as NaN.
  if (trimmed.startsWith('VITE_')) return fallback;
  // Strict digits-only check. parseInt('15m', 10) === 15 would silently turn
  // a user-friendly "15m" (someone reading the JWT_*_EXPIRY duration syntax)
  // into 15 ms — an instant-logout footgun. Reject anything non-numeric.
  if (!/^\d+$/.test(trimmed)) return fallback;
  const parsed = Number(trimmed);
  return parsed > 0 ? parsed : fallback;
}

const RAW_TIMEOUT_MS = parseMsEnv(import.meta.env.VITE_INACTIVITY_TIMEOUT_MS, DEFAULT_TIMEOUT_MS);
const RAW_WARNING_MS = parseMsEnv(import.meta.env.VITE_INACTIVITY_WARNING_MS, DEFAULT_WARNING_MS);

// Misconfiguration guard: warning must precede timeout. If not, fall back
// to defaults rather than silently breaking the modal contract.
const guardOk = RAW_WARNING_MS < RAW_TIMEOUT_MS;
if (!guardOk) {
  console.warn(
    `[authStore] VITE_INACTIVITY_WARNING_MS (${String(RAW_WARNING_MS)}) must be less than ` +
      `VITE_INACTIVITY_TIMEOUT_MS (${String(RAW_TIMEOUT_MS)}); falling back to defaults ` +
      `(${String(DEFAULT_TIMEOUT_MS)} / ${String(DEFAULT_WARNING_MS)}).`,
  );
}
const INACTIVITY_TIMEOUT_MS = guardOk ? RAW_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
const INACTIVITY_WARNING_MS = guardOk ? RAW_WARNING_MS : DEFAULT_WARNING_MS;

interface AuthStore extends AuthState {
  error: string | null;
  inactivityWarningOpen: boolean;
  login: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  isAuthenticated: () => boolean;
  setAuthError: (error: string) => void;
  clearAuthError: () => void;
  resetInactivityTimer: () => void;
  clearInactivityTimer: () => void;
  openInactivityWarning: () => void;
  dismissInactivityWarning: () => void;
}

let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let inactivityWarningTimer: ReturnType<typeof setTimeout> | null = null;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      error: null,
      inactivityWarningOpen: false,

      login: (user: AuthUser, accessToken: string, refreshToken: string) => {
        set({ user, token: accessToken, refreshToken, error: null });
        get().resetInactivityTimer();
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ token: accessToken, refreshToken });
      },

      logout: () => {
        get().clearInactivityTimer();
        set({
          user: null,
          token: null,
          refreshToken: null,
          error: null,
          inactivityWarningOpen: false,
        });
      },

      isAuthenticated: () => get().token !== null,

      setAuthError: (error: string) => {
        set({ error });
      },

      clearAuthError: () => {
        set({ error: null });
      },

      openInactivityWarning: () => {
        set({ inactivityWarningOpen: true });
      },

      dismissInactivityWarning: () => {
        set({ inactivityWarningOpen: false });
      },

      resetInactivityTimer: () => {
        if (inactivityTimer !== null) clearTimeout(inactivityTimer);
        if (inactivityWarningTimer !== null) clearTimeout(inactivityWarningTimer);
        // Dismiss any stale warning modal: the user just interacted, so the
        // session is alive again. Critical for the App.tsx click-listener path
        // where any document click resets the timer — without this, the modal
        // would stay open until manually dismissed.
        if (get().inactivityWarningOpen) {
          set({ inactivityWarningOpen: false });
        }
        inactivityWarningTimer = setTimeout(() => {
          get().openInactivityWarning();
        }, INACTIVITY_TIMEOUT_MS - INACTIVITY_WARNING_MS);
        inactivityTimer = setTimeout(() => {
          get().logout();
        }, INACTIVITY_TIMEOUT_MS);
      },

      clearInactivityTimer: () => {
        if (inactivityTimer !== null) {
          clearTimeout(inactivityTimer);
          inactivityTimer = null;
        }
        if (inactivityWarningTimer !== null) {
          clearTimeout(inactivityWarningTimer);
          inactivityWarningTimer = null;
        }
        if (get().inactivityWarningOpen) {
          set({ inactivityWarningOpen: false });
        }
      },
    }),
    {
      name: 'cbs-auth-storage',
      storage: {
        getItem: (name) => {
          const value = sessionStorage.getItem(name);
          return value ? JSON.parse(value) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
      partialize: (value) =>
        ({
          user: value.user,
          token: value.token,
          refreshToken: value.refreshToken,
        }) as unknown as AuthStore,
      onRehydrateStorage: () => (state) => {
        // Re-arm inactivity timer after rehydration from sessionStorage
        if (state?.token) {
          state.resetInactivityTimer();
        }
      },
    },
  ),
);

// Register with API client so it can read token and call logout on 401
registerAuthStore(() => useAuthStore.getState());

// Exported for tests and for the warning modal countdown
export const INACTIVITY_TIMEOUT_MS_RESOLVED = INACTIVITY_TIMEOUT_MS;
export const INACTIVITY_WARNING_MS_RESOLVED = INACTIVITY_WARNING_MS;
